// External Packages
import { useState, useContext, Fragment } from "react";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { UtilityContext } from "../../context/util-context";

// Internal Components
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";
import { requestAdminAPI } from "../../api/posh-api";
import { toTitleCase } from "../../util/format-text";
import Heading from "../UI/Heading";
import LoadingSpinner from "../UI/LoadingSpinner";
import DetailPair from "../UI/DetailPair";
import Modal from "../UI/Modal";
import { MdClose } from "react-icons/md";

/** ========================================================================
 * * Add Location Page
 * Renders a page, which allows adding a new lcoations to the database.
 * ========================================================================== */

export default function AddLocation({ open, setOpen }) {
  // Load Contexts
  const { navigate } = useContext(UtilityContext);
  const { fetchLocations } = useContext(DataContext);

  // Initialize Processing States
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ==================================== SUBMIT HANDLER
  const submitHandler = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const city = event.target.city.value;
      const state = event.target.state.value;
      const zip = event.target.zip.value;
      const name = `${toTitleCase(city)}, ${state.toUpperCase()}`;
      const id = `${city
        .substring(0, 1)
        .toUpperCase()}${state.toUpperCase()}${zip}`;
      const image = event.target.image.files[0];

      if (!image)
        throw new Error(
          "Please upload an image for the location to display on the website."
        );

      const imageUrl = image
        ? await uploadImageToFirebase(
            `locations/${id}`,
            "image",
            image,
            setUploading
          )
        : "";

      // Create a payload for all location information
      const payload = {
        id,
        name,
        address: `${event.target.street.value}, ${city}, ${state} ${zip}`,
        phone: event.target.phone.value,
        tax: event.target.tax.value,
        deliveryRate: event.target.deliveryRate.value,
        image: imageUrl,
      };
      if (event.target.googleMapsLink.value)
        payload.googleMapsLink = event.target.googleMapsLink.value;
      if (event.target.appleMapsLink.value)
        payload.appleMapsLink = event.target.appleMapsLink.value;
      if (event.target.googleIframe.value)
        payload.googleIframe = event.target.googleIframe.value;

      // Add the Location to the database
      const response = await requestAdminAPI("add-location", payload);

      // Fetch the Location again to update the list
      fetchLocations(() => {
        navigate(`/locations/info?id=${response}`);
        setOpen(false);
        setLoading(false);
      });
    } catch (err) {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[45vw]">
        <Heading title="Add Location" isBackArrow={false}>
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <form onSubmit={submitHandler} className="grid gap-8">
          <div className="info">
            <div className="lg:col-span-2">
              <DetailPair
                label="Street"
                edit={true}
                inputOptions={{
                  type: "text",
                  required: true,
                }}
              />
            </div>
            <DetailPair
              label="City"
              edit={true}
              inputOptions={{
                type: "text",
                required: true,
              }}
            />
            <DetailPair
              label="State"
              edit={true}
              inputOptions={{
                type: "text",
                required: true,
                maxLength: 2,
              }}
            />
            <DetailPair
              label="Zip Code"
              edit={true}
              inputOptions={{
                type: "text",
                name: "zip",
                required: true,
                maxLength: 5,
              }}
            />
            <DetailPair
              label="Phone"
              edit={true}
              inputOptions={{
                type: "text",
                name: "phone",
                required: true,
              }}
            />
            <DetailPair
              label="Tax Rate"
              edit={true}
              inputOptions={{
                type: "number",
                name: "tax",
                max: "100",
                step: "0.01",
                required: true,
              }}
            />
            <DetailPair
              label="Delivery Rate"
              edit={true}
              inputOptions={{
                type: "number",
                name: "deliveryRate",
                required: true,
              }}
            />

            <DetailPair
              label="Image"
              edit={true}
              inputOptions={{
                type: "file",
                name: "image",
                accept: "image/*",
                required: true,
              }}
            />

            <DetailPair
              label="Google Maps Link"
              edit={true}
              inputOptions={{
                type: "text",
                name: "googleMapsLink",
              }}
            />
            <DetailPair
              label="Apple Maps Link"
              edit={true}
              inputOptions={{
                type: "text",
                name: "appleMapsLink",
              }}
            />
            <DetailPair
              label="Google iFrame Script"
              edit={true}
              inputOptions={{
                type: "text",
                name: "googleIframe",
              }}
            />
          </div>
          {loading ? (
            <div className="flex items-center space-x-4 text-sm">
              {uploading && (
                <p className="text-stone-200">
                  Uploading {uploading[0]}.... {uploading[1]}%
                </p>
              )}
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="btn-secondary" onClick={() => setOpen(false)}>
                Cancel
              </div>
              <button className="btn-primary">Submit</button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
}
